// Define the type for the customization options
export interface AgencyConfiguration {
  // Basic styling using React's CSSProperties
  styling?: React.CSSProperties;
  // Component customization
  top?: React.ComponentType<any>;
  left?: React.ComponentType<any>;
  widgetTitleOverrideKey?: string;
  headerTitleOverrideKey?: string;
  aiChipTextOverrideKey?: string;
  startingMessageOverrideKey?: string;
  closedWidgetCloseButtonClassName?: string;
  closedWidgetCloseIconClassName?: string;
  preventRefreshOnUrlChange?: boolean;
  enableEmailCollection?: boolean;
  enableEmailCollectionOnlyAgency?: boolean;
  askEmail?: boolean;
  customEmailMessage?: string;
  customEmailPrompt?: string;
  customThankYouMessage?: string;
  closedWidgetStyling?: React.CSSProperties;
  swapLogoWithAI?: boolean;
  swapLogoWithArrow?: boolean;
  closedWidgetCtaConfig?: {
    useIcon?: boolean;
    colorClassName?: string;
    AIwhite?: boolean;
  };
  headerStyling?: {
    type?: 'gradient' | 'corner';
    backgroundColor?: string;
    textColor?: string;
    background?: string;
    backgroundBlendMode?: string;
  };
  contactPromptOverrideKey?: string;
  // Timeframe options filtering
  excludedTimeframeOptions?: ('now' | '6m' | '12m' | 'curious' | 'no')[];
  // Enable inline follow-up (move plan) after timeframe selection in Step 6
  enableTimeframeFollowUp?: boolean;
  // Custom valuation component
  enableCustomValuationComponent?: boolean;
  customValuationMessage?: string;
  customValuationButton?: string;
  customValuationTimer?: number; // Timer in milliseconds
  // Agent contact notice control
  agentContactNoticeNull?: boolean; // If true, hides the agent contact notice text
  // Widget dismissal behavior
  hideWidgetWhenDismissed?: boolean; // If true, completely hide instead of showing circle
  showCircleOnUrls?: string[];
  // Add this new property for deactivation
  isDeactivated?: boolean;
  // Blurred value confirmation before revealing estimate
  enableValuationConfirmation?: boolean;
  // Property transaction form configuration
  askAsbestosAndFinishLevel?: boolean;
  // Skip valuation display and show contact message instead
  noValuation?: boolean;
  // Other configuration
  addRentingFlow?: boolean;
  options?: {
    widgetPosition?: 'right' | 'left' | 'center';
    animationDuration?: number;
  };
}

// Sample top component for a specific agency
export const SampleTopComponent: React.FC<{
  onOpenChat?: () => void;
}> = ({ onOpenChat }) => (
  <div style={{ fontFamily: 'serif' }}>
    <button
      onClick={() => onOpenChat?.()}
      className="block w-full mb-2 px-4 py-2.5 bg-gray-500 text-white rounded-full hover:bg-gray-400 transition-colors text-sm font-medium shadow-sm"
    >
      Sell House
    </button>
    <button
      onClick={() => onOpenChat?.()}
      className="block w-full px-4 py-2.5 bg-gray-500 text-white rounded-full hover:bg-gray-400 transition-colors text-sm font-medium shadow-sm"
    >
      Get Valuation
    </button>
  </div>
);

// Sample left component for a specific agency
export const SampleLeftComponent: React.FC = () => (
  <div className="absolute -left-16 top-1/2 -translate-y-1/2 flex items-center">
    <svg 
      width="48" 
      height="48" 
      viewBox="0 0 24 24" 
      fill="none" 
      xmlns="http://www.w3.org/2000/svg"
      className="text-gray-500"
    >
      <path 
        d="M9 5L16 12L9 19" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

// Map of agency-specific customizations
export const agencyCustomizations: Record<string, AgencyConfiguration> = {
  'test': {
    styling: {
      backgroundColor: '#8cc6d9',
      borderRadius: '50px',
      color: '#335566',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      fontFamily: 'serif'
    },
    top: SampleTopComponent,
    left: SampleLeftComponent,
    options: {
      animationDuration: 300
    }
  },
  /*'agency001': {
    closedWidgetStyling: {
      background: `
      linear-gradient(90deg, hsl(12 90% 50%) 0%, hsl(18 80% 60%) 30%, hsl(25 70% 75%) 50%, hsl(35 60% 85%) 70%, hsl(40 50% 92%) 80%, hsl(45 30% 96%) 85%, hsl(50 20% 98%) 90%, white 95%),
      url("data:image/svg+xml,%3Csvg viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")
    `,
      backgroundBlendMode: 'overlay',
      color: 'black',
      borderColor: 'black'
    },
    headerStyling: {
      type: 'gradient',
      background: `
      linear-gradient(180deg, hsl(12 90% 50%) 0%, hsl(18 80% 60%) 30%, hsl(25 70% 75%) 60%, hsl(35 60% 85%) 85%, hsl(40 30% 98%) 100%),
      url("data:image/svg+xml,%3Csvg viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")
      `,
      backgroundBlendMode: 'overlay',
      textColor: 'black'
    }
  },*/
  'agency001': {
  },
  'agency004': {
    askEmail: true,
    closedWidgetStyling: {
      backgroundColor: '#5dbea7',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#5dbea7]',
      AIwhite: true
    }
  },
  'agency011': {
    closedWidgetStyling: {
      backgroundColor: '#5D1E31',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#5D1E31]',
      AIwhite: true
    }
  },
  'agency012': {
    closedWidgetStyling: {
      backgroundColor: '#f8ff01',
      color: 'black',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-black text-black hover:bg-white hover:text-[#f8ff01]',
      AIwhite: true
    },
    enableTimeframeFollowUp: true
  },
  'agency013': {
    closedWidgetStyling: {
      backgroundColor: '#00A3DB',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#00A3DB]',
      AIwhite: true
    }
  },
  'agency014': {
    closedWidgetStyling: {
      backgroundColor: '#FFAC2D',
      color: '#0D3F5F',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-black text-black hover:bg-white hover:text-[#FFAC2D]',
      AIwhite: true
    },
    enableTimeframeFollowUp: true
  },
  'agency017': {
    closedWidgetStyling: {
      backgroundColor: '#E2251D',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#E2251D]',
      AIwhite: true
    }
  },
  'agency019': {
    closedWidgetStyling: {
      backgroundColor: '#fa0000',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#fa0000]',
      AIwhite: true
    }
  },
  'agency020': {
    widgetTitleOverrideKey: 'widgetTitle_agency020',
    headerTitleOverrideKey: 'headerTitle_agency020',
  },
  'agency021': {
    closedWidgetCloseButtonClassName: 'w-6 h-6',
    closedWidgetCloseIconClassName: 'h-4 w-4',
  },
  'agency022': {
    isDeactivated: true,
  },
  'agency023': {
    enableEmailCollection: true,
    closedWidgetStyling: {
      backgroundColor: '#182C4B',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#182C4B]',
      AIwhite: true
    }
  },
  'agency025': {
    enableEmailCollectionOnlyAgency: true,
    customEmailMessage: 'customEmailMessage',
    customEmailPrompt: 'customEmailPrompt',
    customThankYouMessage: 'customThankYouMessage',
    closedWidgetStyling: {
      backgroundColor: '#D71920',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#D71920]',
      AIwhite: true
    }
  },
  'agency026': {
    widgetTitleOverrideKey: 'widgetTitle_agency026',
    startingMessageOverrideKey: 'startingMessage_agency026',
    aiChipTextOverrideKey: 'aiChipText_agency026',
    contactPromptOverrideKey: 'contactPrompt_agency026',
    closedWidgetStyling: {
      backgroundColor: '#BD1622',
      color: 'white',
      borderColor: 'white'
    },
    swapLogoWithAI: true,
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#BD1622]'
    },
    headerStyling: {
      type: 'corner',
      backgroundColor: '#BD1622',
      textColor: 'white'
    },
    askAsbestosAndFinishLevel: true
  },
  'agency027': {
    startingMessageOverrideKey: 'startingMessage_agency027',
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'bg-[#8FC73E] border-1 border-black text-black hover:bg-[#8FC73E]/90'
    },
    closedWidgetStyling: {
      backgroundColor: '#8FC73E',
      color: 'black',
      borderColor: 'white'
    },
  },
  'agency028': {
    closedWidgetStyling: {
      backgroundColor: '#000083',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#000083]',
      AIwhite: true
    },
    preventRefreshOnUrlChange: true,
    excludedTimeframeOptions: ['curious']
  },
  'agency029': {
    closedWidgetStyling: {
      backgroundColor: '#14406F',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#14406F]',
      AIwhite: true
    },
    widgetTitleOverrideKey: 'widgetTitle_agency029',
    excludedTimeframeOptions: ['curious'],
    enableCustomValuationComponent: true,
    customValuationMessage: 'customValuationMessage_agency029',
    customValuationButton: 'customValuationButton_agency029',
    customValuationTimer: 300000, // Currently 5 minutes, change to 600000 for 10 minutes later
    agentContactNoticeNull: true,
    hideWidgetWhenDismissed: true,
    showCircleOnUrls: ['https://www.landbergh.be/vastgoedexpertise/waardebepaling', 'https://www.landbergh.be/vastgoedexpertise/vastgoedstyling', 'https://www.landbergh.be/vastgoedexpertise/professionele-foto-video-en-dronereportage', 'https://www.landbergh.be/vastgoedexpertise/marketingstrategie', 'https://www.landbergh.be/vastgoedexpertise/woning-verkopen'],
  },
  'agency030': {
    closedWidgetStyling: {
      backgroundColor: '#4260AA',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#4260AA]',
      AIwhite: true
    }
  },
  'agency031': {
    closedWidgetStyling: {
      backgroundColor: '#283583',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#283583]',
      AIwhite: true
    }
  },
  'agency032': {
    closedWidgetStyling: {
      backgroundColor: '#94C93D',
      color: 'black',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-black text-black hover:bg-black hover:text-[#94C93D]',
      AIwhite: true
    }
  },
  'agency033': {
    closedWidgetStyling: {
      backgroundColor: '#B28A50',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#B28A50]',
      AIwhite: true
    },
    enableValuationConfirmation: true,
    agentContactNoticeNull: true,
  },
  'agency034': {
    closedWidgetStyling: {
      backgroundColor: '#E42313',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#E42313]',
      AIwhite: true
    }
  },
  'agency035': {
    closedWidgetStyling: {
      backgroundColor: '#69B12E',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#69B12E]',
      AIwhite: true
    }
  },
  'agency036': {
    closedWidgetStyling: {
      backgroundColor: '#000B8E',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#000B8E]',
      AIwhite: true
    }
  },
  'agency037': {
    closedWidgetStyling: {
      backgroundColor: '#C3E828',
      color: 'black',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-black text-black hover:bg-black hover:text-[#C3E828]',
      AIwhite: true
    },
    enableEmailCollectionOnlyAgency: true,
    customEmailMessage: 'customEmailMessage',
    customEmailPrompt: 'customEmailPrompt',
    customThankYouMessage: 'customThankYouMessage'
  },
  'agency038': {
    closedWidgetStyling: {
      backgroundColor: '#1A7FFF',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#1A7FFF]',
      AIwhite: true
    }
  },
  'agency039': {
    closedWidgetStyling: {
      backgroundColor: '#E35D41',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#E35D41]',
      AIwhite: true
    }
  },
  'agency040': {
    closedWidgetStyling: {
      backgroundColor: '#8B8935',
      color: 'white',
      borderColor: 'white'
    },
    closedWidgetCtaConfig: {
      useIcon: true,
      colorClassName: 'border-2 border-white text-white hover:bg-white hover:text-[#8B8935]',
      AIwhite: true
    }
  },
  'agency041': {
    contactPromptOverrideKey: 'contactPrompt_agency041'
  },
  'agency045': {
    noValuation: true,
    agentContactNoticeNull: true,
    enableEmailCollectionOnlyAgency: true,
    customEmailMessage: 'customEmailMessage',
    customEmailPrompt: 'customEmailPrompt',
    customThankYouMessage: 'customThankYouMessage'
  },
  'agency050': {
    addRentingFlow: true,
    noValuation: true,
    agentContactNoticeNull: true
  }
};

// Helper function to get customization by agency ID
export function getAgencyConfig(agencyId?: string, urlConfig?: { backgroundColor?: string; textStyle?: React.CSSProperties }): AgencyConfiguration {
  // Default closedWidgetStyling that's always provided
  const defaultClosedWidgetStyling = {
    backgroundColor: 'white',
    color: 'black', 
    borderColor: 'white'
  };

  // Get agency-specific config (or empty object if no agencyId)
  const agencyConfig = agencyId ? agencyCustomizations[agencyId] || {} : {};
  
  // Always provide closedWidgetStyling with proper fallbacks
  return {
    ...agencyConfig,
    closedWidgetStyling: {
      ...defaultClosedWidgetStyling,
      // Override with URL config values if available
      ...(urlConfig && {
        backgroundColor: urlConfig.backgroundColor || defaultClosedWidgetStyling.backgroundColor,
        color: urlConfig.textStyle?.color || defaultClosedWidgetStyling.color,
      }),
      // Allow agency-specific overrides (highest priority)
      ...agencyConfig.closedWidgetStyling
    }
  };
} 